import React from 'react';
import EngagementCard from 'components/wrapperEventManager/engagementCard.component';
import { User } from 'interfaces/user.interface';
import { EventCommitment } from 'interfaces/types.interface';
import { CrossIcon } from '@common_components/icons/cross.icon';
import { EditBoxIcon } from '@common_components/icons/editBox.icon';
import { PlusIcon } from '@common_components/icons/plus.icon';
import { useTranslation } from 'react-i18next';
import { dayjs, FORMAT_DATE_API, UUID } from '@umanforge/common';
import { StyleEventCard } from 'enum/styleEventCard.enum';

interface DragDropEngagementProps {
  canDrag?: boolean;
  canMove?: boolean;
  isSameDragOver: ( userOver: User, dayOver: Date ) => boolean;
  userOver: User;
  dayOver: Date;
  duplicatedUsers: { id: UUID, fullname: string }[];
  setDragMoveOrLinkEngagement: ( value: number ) => void;
  handleDropToMoveEngagement: ( e: React.DragEvent, dayCell: Date ) => void;
  handleResetOnDropDisable: () => void;
  handleDropToAddEngagementToUser: ( e: React.DragEvent, dayCell: Date ) => void;
  draggedEngagement: EventCommitment | null;
  draggedParticipantStart: User;
  isDragging: boolean;
  userCell: User;
  dayCell: Date;
  dragMoveOrLinkEngagement: number;
}

const CellCardInteractionDrag: React.FC<DragDropEngagementProps> = ( {
  isSameDragOver,
  userOver,
  duplicatedUsers,
  dayOver,
  isDragging,
  draggedParticipantStart,
  setDragMoveOrLinkEngagement,
  handleDropToMoveEngagement,
  handleDropToAddEngagementToUser,
  handleResetOnDropDisable,
  draggedEngagement,
  userCell,
  dayCell,
  dragMoveOrLinkEngagement,
  canDrag,
  canMove,
} ) => {
  const { t } = useTranslation();
  const userAlreadyHasEvent = duplicatedUsers.length > 0;

  const canShare = ( userCell.id !== draggedParticipantStart.id ) || ( draggedEngagement && dayjs( draggedEngagement.deliveryDate ).format( FORMAT_DATE_API ) !== dayjs( dayOver ).format( FORMAT_DATE_API ) );

  if ( typeof canDrag !== 'undefined' && !canDrag ) {
    return null;
  }

  return (
    <div
      className={`absolute w-full h-full rounded-md text-orange-base flex-center
        ${isSameDragOver( userOver, dayOver ) ? 'z-10' : '-z-10'}`}
      onDragOver={() => {
        if ( !userAlreadyHasEvent && !canMove ) {
          return setDragMoveOrLinkEngagement( 1 );
        }
      }}
      onDrop={e => {
        if ( !userAlreadyHasEvent && !canMove ) {
          return handleDropToAddEngagementToUser( e, dayCell );
        }
      }}
    >
      {
        userAlreadyHasEvent ?
          (
            <div
              className="w-full h-full p-2 z-40"
              onDrop={handleResetOnDropDisable}
            >
              <div className="w-full h-full flex-col bg-white rounded-xl flex-center text-red-base-dark pointer-events-none">
                <CrossIcon className="p-2 rounded-md shadow-md bg-red-100" />
                <p className="text-center mt-3">
                  {
                    duplicatedUsers.length === 1 ?
                      t( 'translation:commitment_already_shared_with_the_user' ).replace( '{fullname}', duplicatedUsers[ 0 ].fullname )
                      :
                      t( 'translation:commitment_already_shared_with_the_users' ).replace( '{number}', String( duplicatedUsers.length ) )
                  }
                </p>
              </div>
            </div>
          )
          :
          isDragging ?
            (
              <>
                {/* Div Move */}
                {
                  canMove ?
                    <div
                      className="w-full h-full trans-fast p-2 z-10"
                      onDragOver={() => {return setDragMoveOrLinkEngagement( 0 );}}
                      onDrop={e => {return handleDropToMoveEngagement( e, dayCell );}}
                    >
                      <div className="w-full relative h-full bg-white rounded-md flex items-end justify-center shadow-md">
                        <div
                          className={`-translate-y-4 flex flex-col gap-2 flex-center trans-fast pointer-events-none ${
                            dragMoveOrLinkEngagement === 0 ? 'translate-y-14' : ''
                          }`}
                        >
                          <p className="font-texte">{ t( 'translation:move' ) }</p>
                          <EditBoxIcon
                            size={10}
                            className="p-3 bg-[#ffe1d8] rounded-md shadow-md"
                          />
                        </div>
                      </div>
                    </div>
                    :
                    <div
                      className="w-full h-full p-2 z-40"
                      onDrop={handleResetOnDropDisable}
                    >
                      <div className="w-full h-full flex-col bg-white rounded-xl flex-center text-red-base-dark pointer-events-none">
                        <CrossIcon className="p-2 rounded-md shadow-md bg-red-100" />
                        <p className="text-center mt-3">
                          {t( 'translation:the_user_is_not_on_all_event_managers' )}
                        </p>
                      </div>
                    </div>
                }

                {/* Div Link */}
                <div
                  className={`h-full trans-fast p-2 z-10 w-full ${
                    canShare ? 'flex' : 'hidden'
                  }`}
                  onDragOver={() => {return setDragMoveOrLinkEngagement( 1 );}}
                  onDrop={e => {return handleDropToAddEngagementToUser( e, dayCell );}}
                >
                  <div className="w-full h-full bg-white rounded-md flex items-end justify-center shadow-md">
                    <div className="w-full relative h-full bg-white rounded-md flex items-end justify-center shadow-md">
                      <div
                        className={`-translate-y-4 flex flex-col gap-2 flex-center trans-fast pointer-events-none ${
                          dragMoveOrLinkEngagement === 1 ? 'translate-y-14' : ''
                        }`}
                      >
                        <p className="font-texte">{ t( 'translation:share' ) }</p>
                        <PlusIcon
                          size={10}
                          className="p-3 bg-[#ffe1d8] rounded-md shadow-md"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Dragged Engagement */}
                <div
                  className={`
                    absolute z-40 trans-fast text-black pointer-events-none -translate-y-4
                    ${ dragMoveOrLinkEngagement === 0 && canShare ? '-translate-x-20 -rotate-[20deg] -translate-y-4' : '' }
                    ${ dragMoveOrLinkEngagement === 1 && canShare ? 'translate-x-20 rotate-[20deg] -translate-y-4' : '' }
                  `}
                >
                  {draggedEngagement && (
                    <EngagementCard
                      key="dragEvent"
                      user={userCell}
                      engagement={draggedEngagement}
                      isHighlighted={Boolean( draggedEngagement.highlighted )}
                      engagementIndex={2}
                      dayStart={dayCell}
                      style={ StyleEventCard.square }
                    />
                  )}
                </div>
              </>
            )
            :
            null
      }
    </div>
  );
};

export default CellCardInteractionDrag;
